/**
 * API Cache Manager
 * Implements cache-on-GET, invalidate-on-mutation strategy
 * Industry standard approach used by major web applications
 */

class ApiCache {
  constructor() {
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
    
    // Define cache keys and their TTL
    this.cacheConfig = {
      '/api/profile/get': { ttl: 10 * 60 * 1000 }, // 10 min - profile changes less frequently
      '/api/applications/get': { ttl: 2 * 60 * 1000 }, // 2 min - applications change more often
      '/api/work-history/get': { ttl: 10 * 60 * 1000 }, // 10 min - work history stable
      '/api/RolesRpc/GetRoles': { ttl: 30 * 60 * 1000 }, // 30 min - roles rarely change
      '/api/templates/': { ttl: 15 * 60 * 1000 }, // 15 min - templates stable
      '/api/pricing/': { ttl: 60 * 60 * 1000 }, // 1 hour - pricing changes rarely
    };
  }

  /**
   * Generate cache key from URL and method
   */
  getCacheKey(url, method = 'GET', userId = null) {
    const baseKey = `${method}:${url}`;
    return userId ? `${baseKey}:${userId}` : baseKey;
  }

  /**
   * Get TTL for a specific endpoint
   */
  getTTL(url) {
    for (const [pattern, config] of Object.entries(this.cacheConfig)) {
      if (url.includes(pattern)) {
        return config.ttl;
      }
    }
    return this.defaultTTL;
  }

  /**
   * Check if cached data is still valid
   */
  isValid(key) {
    if (!this.cache.has(key)) return false;
    
    const timestamp = this.cacheTimestamps.get(key);
    const ttl = this.getTTL(key);
    
    return timestamp && (Date.now() - timestamp) < ttl;
  }

  /**
   * Get cached data if valid
   */
  get(url, method = 'GET', userId = null) {
    const key = this.getCacheKey(url, method, userId);
    
    if (this.isValid(key)) {
      console.log(`🎯 Cache HIT: ${url}`);
      return this.cache.get(key);
    }
    
    console.log(`❌ Cache MISS: ${url}`);
    return null;
  }

  /**
   * Store data in cache
   */
  set(url, data, method = 'GET', userId = null) {
    const key = this.getCacheKey(url, method, userId);
    this.cache.set(key, data);
    this.cacheTimestamps.set(key, Date.now());
    console.log(`💾 Cached: ${url}`);
  }

  /**
   * Invalidate cache entries by pattern
   * Called when user performs mutations (save, update, delete)
   */
  invalidate(patterns) {
    if (typeof patterns === 'string') {
      patterns = [patterns];
    }

    let invalidatedCount = 0;
    
    for (const key of this.cache.keys()) {
      for (const pattern of patterns) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
          this.cacheTimestamps.delete(key);
          invalidatedCount++;
          console.log(`🗑️ Invalidated: ${key}`);
        }
      }
    }
    
    console.log(`🧹 Invalidated ${invalidatedCount} cache entries`);
  }

  /**
   * Clear all cache
   */
  clear() {
    const count = this.cache.size;
    this.cache.clear();
    this.cacheTimestamps.clear();
    console.log(`🧹 Cleared ${count} cache entries`);
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      timestamps: Array.from(this.cacheTimestamps.entries())
    };
  }
}

// Create singleton instance
export const apiCache = new ApiCache();

/**
 * Enhanced fetch wrapper with caching
 * Only caches GET requests, passes through all others
 */
export const cachedFetch = async (url, options = {}) => {
  const method = options.method || 'GET';
  
  // Only cache GET requests
  if (method.toUpperCase() !== 'GET') {
    return fetch(url, options);
  }

  // Extract user ID from token for user-specific caching
  const token = localStorage.getItem('token');
  let userId = null;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId = payload.sub || payload.user_id || payload.id;
    } catch (e) {
      // Invalid token, continue without user ID
    }
  }

  // Check cache first
  const cached = apiCache.get(url, method, userId);
  if (cached) {
    // Return cached response immediately
    return new Response(JSON.stringify(cached), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Make actual request
  const response = await fetch(url, options);
  
  // Cache successful responses
  if (response.ok) {
    const data = await response.clone().json();
    apiCache.set(url, data, method, userId);
  }

  return response;
};

/**
 * Synchronous cache check - returns cached data immediately if available
 * Use this to avoid showing loading states when data is already cached
 */
export const getCachedData = (url, method = 'GET') => {
  const token = localStorage.getItem('token');
  let userId = null;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId = payload.sub || payload.user_id || payload.id;
    } catch (e) {
      // Invalid token, continue without user ID
    }
  }

  return apiCache.get(url, method, userId);
};

/**
 * Cache invalidation helper for mutations
 * Call this after successful save/update operations
 */
export const invalidateCache = (patterns) => {
  apiCache.invalidate(patterns);
};

// Export cache instance for direct access
export default apiCache;